import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { requireAdmin } from "@/lib/admin-guard";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { listTemplates, getTemplate, createContract } from "@/lib/contracts.functions";
import { ContractBuilder, emptyContract, type ContractFormState } from "@/components/admin/ContractBuilder";
import { validateHourlyRateCurrency } from "@/lib/contract-currency-guard";

export const Route = createFileRoute("/admin_/contracts/new")({
  ssr: false,
  head: () => ({ meta: [{ title: "New contract — Admin" }, { name: "robots", content: "noindex,nofollow" }] }),
  beforeLoad: requireAdmin,
  component: NewContract,
});

function NewContract() {
  const nav = useNavigate();
  const list = useServerFn(listTemplates);
  const getTpl = useServerFn(getTemplate);
  const create = useServerFn(createContract);

  const { data: templates } = useQuery({
    queryKey: ["contract-templates"],
    queryFn: () => list(),
  });

  const [tplId, setTplId] = useState<string>("");
  const [value, setValue] = useState<ContractFormState>(emptyContract);
  const [loading, setLoading] = useState(false);

  const applyTemplate = async (id: string) => {
    setTplId(id);
    if (!id) { setValue(emptyContract); return; }
    setLoading(true);
    try {
      const { template } = await getTpl({ data: { id } });
      if (template) {
        setValue({
          ...emptyContract,
          title: template.name,
          type: template.type,
          body: Array.isArray(template.body) ? (template.body as any) : [],
          ...(template.default_terms && typeof template.default_terms === "object" ? template.default_terms : {}),
        });
      }
    } catch (e: any) {
      toast.error(e.message);
    } finally { setLoading(false); }
  };

  const createMut = useMutation({
    mutationFn: (v: ContractFormState) => create({
      data: {
        template_id: tplId || null,
        title: v.title,
        type: v.type as any,
        counterparty_name: v.counterparty_name,
        counterparty_email: v.counterparty_email || null,
        counterparty_role: v.counterparty_role || null,
        counterparty_company: v.counterparty_company || null,
        company_name: v.company_name,
        start_date: v.start_date || null,
        end_date: v.end_date || null,
        notice_period: v.notice_period || null,
        payment_terms: v.payment_terms || null,
        payment_frequency: v.payment_frequency || null,
        hourly_rate: v.hourly_rate ? parseFloat(v.hourly_rate) : null,
        hourly_rate_currency: "USD",
        revenue_share: v.revenue_share,
        governing_law: v.governing_law || null,
        notes: v.notes || null,
        body: v.body,
        custom_sections: v.custom_sections,
      },
    }),
    onSuccess: (res) => {
      toast.success("Contract created");
      nav({ to: "/admin/contracts/$id", params: { id: res.id } });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="min-h-dvh bg-background">
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
        <Link to="/admin/contracts" className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to contracts
        </Link>

        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl">New contract</h1>
            <p className="mt-1 text-sm text-muted-foreground">Start from a template or build from scratch.</p>
          </div>
          <div className="flex items-center gap-3">
            <select value={tplId} onChange={(e) => applyTemplate(e.target.value)} className="rounded-lg border border-border bg-card px-3 py-2 text-sm">
              <option value="">— Blank contract —</option>
              {(templates?.items ?? []).map((t: any) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
            <button
              disabled={loading || createMut.isPending || !value.title || !value.counterparty_name}
              onClick={() => {
                const err = validateHourlyRateCurrency(value.hourly_rate_currency);
                if (err) { toast.error(err); return; }
                createMut.mutate(value);
              }}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {createMut.isPending ? "Creating…" : "Create contract"}
            </button>
          </div>
        </div>

        <ContractBuilder value={value} onChange={setValue} />
      </div>
    </div>
  );
}
